import React from 'react';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMount } from '@shared/utils/hooks/useMount';
import type { PayoutRejectedStepCta } from './PayoutDialog.types';

const SUPPORT_EMAIL_HREF = 'mailto:support@withjoy.com';

type RejectedStepProps = Readonly<{
  /** Stripe Express dashboard link. Present when the couple can resolve the missing information themselves. */
  serviceDashboardUrl?: string | null;
  onViewed?: () => void;
  onInteracted?: (cta: PayoutRejectedStepCta) => void;
}>;

/**
 * Shown when the payout account status is `rejected`. Stripe returns no `onboardingUrl` in that
 * state, so the self-serve route is the Express dashboard when the account has one, and support
 * otherwise.
 */
export const RejectedStep: React.FC<RejectedStepProps> = ({ serviceDashboardUrl, onViewed, onInteracted }) => {
  const { tq } = useTranslation('cashFundsAdmin');

  useMount(() => {
    onViewed?.();
  });

  return (
    <Flex
      flexDirection="column"
      flex={1}
    >
      <TextV2
        textAlign="center"
        fontSize={pxToRem(24)}
        fontWeight={600}
        color="mono14"
        letterSpacing="-0.024em"
        lineHeight="1.3"
        marginBottom={6}
      >
        {tq`More information needed`}
      </TextV2>

      <Flex
        flexDirection="column"
        flex={1}
        justifyContent="center"
      >
        <TextV2
          textAlign="center"
          fontSize={pxToRem(15)}
          color="mono10"
          lineHeight="1.55"
        >
          {serviceDashboardUrl
            ? tq`Stripe needs more information before you can transfer funds to your bank. Add the missing details in your Stripe account, or contact us if you need help.`
            : tq`Stripe needs more information before you can transfer funds to your bank. Our support team can help you find out what's missing.`}
        </TextV2>
      </Flex>

      {serviceDashboardUrl ? (
        <>
          <ButtonV2
            as="a"
            href={serviceDashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="lg"
            intent="neutral"
            shape="rounded"
            fullWidth
            marginTop={6}
            onClick={() => onInteracted?.('manageAccount')}
          >
            {tq`Manage Stripe Account`}
          </ButtonV2>
          <ButtonV2
            as="a"
            href={SUPPORT_EMAIL_HREF}
            variant="ghost"
            intent="neutral"
            size="md"
            shape="rounded"
            fullWidth
            marginTop={2}
            onClick={() => onInteracted?.('contactSupport')}
          >
            {tq`Contact Support`}
          </ButtonV2>
        </>
      ) : (
        <ButtonV2
          as="a"
          href={SUPPORT_EMAIL_HREF}
          size="lg"
          intent="neutral"
          shape="rounded"
          fullWidth
          marginTop={6}
          onClick={() => onInteracted?.('contactSupport')}
        >
          {tq`Contact Support`}
        </ButtonV2>
      )}
    </Flex>
  );
};
